import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const features = [
  { text: 'Find musicians by instrument, genre, and location' },
  { text: 'Connect with bands for rehearsals and live gigs' },
  { text: 'Book fully equipped rehearsal spaces near you' },
  { text: 'Share demos and videos to showcase your sound' },
  { text: 'Post ads and get contacted by like-minded musicians' },
  { text: 'Collaborate on music projects across the country' },
];

// Gallery images pulled from the existing public gallery photos
const mosaicImages = [
  '/gallary-photos/gp1.avif',
  '/gallary-photos/gp2.avif',
  '/gallary-photos/gp3.avif',
  '/gallary-photos/gp4.avif',
  '/gallary-photos/gp5.avif',
];

const CommunitySection = () => {
  const navigate = useNavigate();

  return (
    <section style={{
      background: '#0f172a',
      padding: '20px 24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @media (max-width: 640px) {
          .community-features-grid { grid-template-columns: 1fr !important; }
          .community-content { gap: 32px !important; }
        }
      `}</style>
      {/* subtle background glow */}
      <div style={{
        position: 'absolute', top: '10%', left: '-10%',
        width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(55,78,169,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        maxWidth: '1200px', margin: '0 auto',
        display: 'flex', flexWrap: 'wrap',
        alignItems: 'center', gap: '60px',
      }} className="community-content">
        {/* ── Left: text ── */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ flex: '1 1 380px' }}
        >

          <h2 style={{
            fontFamily: 'Gilroy-semiBold, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 4vw, 2.8rem)',
            color: '#fff',
            lineHeight: 1.2,
            marginBottom: '20px',
            letterSpacing: '-0.02em',
          }}>
            Join a growing<br />community of musicians
          </h2>

          <p style={{
            color: '#94a3b8',
            fontSize: '1.05rem',
            lineHeight: 1.75,
            fontFamily: 'Poppins, sans-serif',
            marginBottom: '32px',
            maxWidth: '480px',
          }}>
            For rehearsals, collaborations, auditions, and new music projects — all in one place.
          </p>

          {/* feature grid */}
          <div className="community-features-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '14px 24px',
            marginBottom: '40px',
          }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{
                  width: 20, height: 20, minWidth: 20,
                  background: 'rgba(55,78,169,0.25)',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: '2px',
                }}>
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                    <path d="M1 4.5L4 7.5L10 1.5" stroke="#818cf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span style={{
                  color: '#cbd5e1',
                  fontSize: '0.9rem',
                  fontFamily: 'Poppins, sans-serif',
                  lineHeight: 1.5,
                }}>
                  {f.text}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/login?signup=1&artist=1')}
            style={{
              background: '#374EA9',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              padding: '13px 32px',
              fontSize: '1rem',
              fontFamily: 'Gilroy-semiBold, sans-serif',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(55,78,169,0.4)',
              transition: 'background 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#4a62d6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#374EA9'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Join the Community →
          </button>
        </motion.div>

        {/* ── Right: mosaic ── */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={{ flex: '1 1 340px', position: 'relative' }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(3, 140px)',
            gap: '10px',
          }}>
            {/* large top-left */}
            <div style={{ gridColumn: '1 / 2', gridRow: '1 / 3', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              <img src={mosaicImages[0]} alt="musician" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {/* top-center */}
            <div style={{ gridColumn: '2 / 3', gridRow: '1 / 2', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }}>
              <img src={mosaicImages[1]} alt="musician" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {/* top-right */}
            <div style={{ gridColumn: '3 / 4', gridRow: '1 / 2', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }}>
              <img src={mosaicImages[2]} alt="musician" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {/* middle-center tall */}
            <div style={{ gridColumn: '2 / 3', gridRow: '2 / 4', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }}>
              <img src={mosaicImages[3]} alt="musician" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {/* bottom-right */}
            <div style={{ gridColumn: '3 / 4', gridRow: '2 / 4', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.35)' }}>
              <img src={mosaicImages[4]} alt="musician" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {/* bottom-left */}
            <div style={{ gridColumn: '1 / 2', gridRow: '3 / 4', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 6px 16px rgba(0,0,0,0.35)', background: 'rgba(55,78,169,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold', fontSize: '1.1rem', textAlign: 'center', padding: '8px' }}>+1000<br/>Musicians</span>
            </div>
          </div>

          {/* decorative dots */}
          <div style={{
            position: 'absolute', bottom: -20, right: -20,
            display: 'grid', gridTemplateColumns: 'repeat(5, 8px)', gap: '6px',
            opacity: 0.3, pointerEvents: 'none',
          }}>
            {Array.from({ length: 25 }).map((_, i) => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: '#818cf8' }} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CommunitySection;
